
import { Field, Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Box, Typography, Divider, Paper, Alert, Stack, styled } from "@mui/material";
import _ from "lodash";
import AddressForm from "@/components/form-fields/AddressForm/AddressForm";
import CheckboxField from "../../form-fields/CheckboxField";
import EmailField, { validEmail } from "../../form-fields/EmailField";
import FileUploadField, { validateFileUpload } from "../../form-fields/FileUploadField";
import LangSelectField from "../../form-fields/LangSelectField";
import RadioGroupField from "../../form-fields/RadioGroupField";
import TextField from "../../form-fields/TextField";
import VATField, { isCompanyVAT, validateVAT } from "../../VATField";
import { resetLeadsErrors } from "../actions/electricityContractation";
import { useTranslation } from "react-i18next";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";
import { useSettingsContext } from "../../Settings/hooks/useSettingsContext";
import { FormProvider, useForm } from "react-hook-form";
import { useContext, useEffect, useRef, useState } from "react";
import { SubmitButtonContext } from "../Contractation";

const NoFlagMuiTelInputStyled = styled(MuiTelInput)`
.MuiTelInput-IconButton {
  display: none;
}
`;

const FormPerson = ({onGoBack, onSubmit, type}) => {
  const submitButtonContext = useContext(SubmitButtonContext);
  const Settings = useSettingsContext();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const contractation = useSelector((state) => state[`${type}Contractation`]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    if(!contractation.steps.installation.activeContract) {
      delete values.changeOwner
    }
    values.company = isCompanyVAT(values.vat);
    values.mobile = removePhonePrefix(values.mobile); 
    if (values.sameInvoiceAddress) {
      values.ownerAddress = contractation.steps.installation.address;
    }
    await onSubmit(values)
    setIsSubmitting(false);
  };

  useEffect(() => {
    submitButtonContext?.setValues({
      onClick: () => {formRef.current.submit()},
      loading: isSubmitting,
      disabled: Object.values([
        contractation.leadErrors.dniInvalid, 
        contractation.leadErrors.badScore
      ]).some((x) => x),
    });
  }, [isSubmitting, handleSubmit, contractation.leadErrors]);

  const isReadOnly = (field) => {
    let r = false;
    if (contractation.config && contractation.config?.[field]) {
      r = true;
    }
    return r;
  };

  const handleVATChange = () => {
    dispatch(resetLeadsErrors())
  }

  const removePhonePrefix = (phone) => {
    return !phone.startsWith("+34") ? phone : phone.slice(3);
  }

  // It is necessary for mui-tel-input to validate correctly
  const addInitialValuePhonePrefix = (phone) => {
    return phone.startsWith("+34") ? phone : '+34' + phone;
  }

  const invoiceAddressMethods = useForm({
    defaultValues: {
      ownerAddress: contractation.steps.person.ownerAddress
    }
  });

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={{
        ...contractation.steps.person,
        ...contractation.config,
        mobile: addInitialValuePhonePrefix(
          contractation.steps.person.mobile ?? contractation.config.mobile ?? ''
        ),
      }}
      validate={(values) =>  {
        const errors = {};
        if (!values.vat) {
          errors.vat = t('common:text.required_field');
        } else if (!validateVAT(values.vat)) {
          errors.vat = t('common:text.vat_field_validation_invalid');
        }

        if (!values.name) {
          errors.name = t('common:text.required_field');
        }

        if (isCompanyVAT(values.vat)) {
          if (Settings.contractation?.restrictCompanyVAT) {
            errors.vat = t('common:text.vat_company_not_allowed');
          }
          if (_.isEmpty(values.vatRepresentante)) {
            _.set(errors, 'vatRepresentante', t('common:text.required_field'));
          } else if (!validateVAT(values.vatRepresentante)) {
            errors.vatRepresentante = t('common:text.vat_field_validation_invalid');
          }

          if (!values.nameRepresentante) {
            errors.nameRepresentante = t('common:text.required_field');
          }

          if (!values.surName1Representante) {
            errors.surName1Representante = t('common:text.required_field');
          }

          const nifEmpresaFaceUpErrors = validateFileUpload(values.nifEmpresa, {
            min: 1,
            minMessage: t('common:text.multiupload_validation_min'),
          });
          if (nifEmpresaFaceUpErrors) {
            _.set(errors, "nifEmpresa", nifEmpresaFaceUpErrors);
          }

        } else {
          if (!values.surName1) {
            errors.surName1 = t('common:text.required_field');
          }
        }

        if (!values.sameInvoiceAddress) {

          // Zipcode
          if (!values.ownerAddress.dp) {
            _.set(errors, "ownerAddress.dp", t('common:text.required_field'));
          } else if (values.ownerAddress.dp.length !== 5) {
            _.set(
              errors,
              "ownerAddress.dp",
              t('common:text.contractation_postal_code_length')
            );
          }

          // Poblacio
          if (!values.ownerAddress.poblacio) {
            _.set(errors, "ownerAddress.poblacio", t('common:text.required_field'));
          }

          // Street Type
          if (!values.ownerAddress.tv) {
            _.set(errors, "ownerAddress.tv", t('common:text.required_field'));
          }

          // Street Name
          if (!values.ownerAddress.nv) {
            _.set(errors, "ownerAddress.nv", t('common:text.required_field'));
          } else if (values.ownerAddress.nv.length >= 256) {
            _.set(
              errors,
              "ownerAddress.nv",
              t('common:text.address_validation_nv_length')
            );
          }

          // Street Number
          if (!values.ownerAddress.pnp) {
            _.set(errors, "ownerAddress.pnp", t('common:text.required_field'));
          } else if (values.ownerAddress.pnp.length >= 9) {
            _.set(
              errors,
              "ownerAddress.pnp",
              t('common:text.address_validation_pnp_length')
            );
          }

          // Block
          if (
            values.ownerAddress.bq &&
            values.ownerAddress.bq.length >= 4
          ) {
            _.set(
              errors,
              "ownerAddress.bq",
              t('common:text.address_validation_bq_length')
            );
          }

          // Stair
          if (
            values.ownerAddress.es &&
            values.ownerAddress.es.length >= 4
          ) {
            _.set(
              errors,
              "ownerAddress.es",
              t('common:text.address_validation_es_length')
            );
          }

          // Floor
          if (
            values.ownerAddress.pt &&
            values.ownerAddress.pt.length >= 10
          ) {
            _.set(
              errors,
              "ownerAddress.pt",
              t('common:text.address_validation_pt_length')
            );
          }

          // Door
          if (
            values.ownerAddress.pu &&
            values.ownerAddress.pu.length >= 4
          ) {
            _.set(
              errors,
              "ownerAddress.pu",
              t('common:text.address_validation_pu_length')
            );
          }
        }

        // changeOwner
        if (
          contractation.steps.installation.activeContract === true && 
          values.changeOwner == null
        ) {
          _.set(
            errors,
            "changeOwner",
            t('common:text.required_field')
          );
        }

        // commsLanguage
        if (
          values.commsLanguage == null &&
          (Settings.contractation.commsLanguage ?? true)
        ) {
          _.set(
            errors,
            "commsLanguage ",
            t('common:text.required_field')
          );
        }

        if (!values.mobile || values.mobile.length < 4) {
          errors.mobile = t('common:text.required_field');
        } else if (!matchIsValidTel(addInitialValuePhonePrefix(values.mobile))) {
          errors.mobile = t('common:text.contractation_mobile_not_valid');
        }

        if ((Settings?.contractation?.electricity?.emailRequired ?? true) && !values.email) {
          errors.email = t('common:text.required_field');
        } else if (!validEmail(values.email)) {
          errors.email = t('common:text.contractation_email_not_valid');
        }

        const nifFaceupErrors = validateFileUpload(values.nifFaceup, {
          min: 1,
          minMessage: t('common:text.multiupload_validation_min'),
        });

        if (Settings?.contractation?.dniRequired !== false && nifFaceupErrors) {
          _.set(errors, "nifFaceup", nifFaceupErrors);
        }

        // invoiceAddressMethods.clearErrors();
        // errors?.ownerAddress && Object.entries(errors.ownerAddress).forEach(err => {
        //   invoiceAddressMethods.setError(`ownerAddress.${err[0]}`, {type: "required", message: err[1]})
        // });

        return errors;
      }}
      render={({ handleSubmit, submitting, values, form }) => {
        formRef.current = form;
        // 'company' es fa servir per canviar el render del form. Fem l'and per restringir-ho quan no acceptem CIFs.
        // TODO: FIX setting
        let company = isCompanyVAT(values.vat) && !(Settings?.contractation?.electricity?.restrictCompanyVAT ?? false);
        return (
          <form onSubmit={handleSubmit}>
            <Box sx={{ml: {md: 5}, mr: {md: 5}}}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4} sx={{pr: {xs: 24, sm: 0}}}>
                  <Typography variant="h6">
                    {t('common:text.contractation_personal_data')}
                  </Typography>
                  <Field
                    name="vat"
                    render={({input: {onChange, onBlur, value}, meta}) => {
                      return (
                        <VATField
                          value={value}
                          onBlur={onBlur}
                          onChange={(value) => {
                            handleVATChange(value);
                            onChange(value)
                          }}
                          error={meta.error}
                          showError={meta.touched}
                          style={{ width: "100%" }}
                          label={
                            t(
                              (Settings?.contractation?.electricity?.restrictCompanyVAT ?? false) ?
                                'common:text.contractation_nif_without_cif'
                              :
                                'common:text.contractation_nif'
                            )
                          }
                        />
                      )
                    }}
                    readOnly={isReadOnly("vat")}
                    style={{width: "100%"}}
                  />
                </Grid>
                <Grid container item xs={12} spacing={3} style={{paddingRight: 0}}>
                  <Grid item xs={12} md={4}>
                    <Field
                      name="name"
                      component={TextField}
                      style={{width: "100%"}}
                      label={
                        company
                          ? t('common:text.contractation_social_reason')
                          : t('common:text.contractation_name')
                      }
                      readOnly={isReadOnly("name")}
                    />
                  </Grid>
                  {!company && (
                    <Grid item xs={12} md={4}>
                      <Field
                        name="surName1"
                        component={TextField}
                        label={t('common:text.contractation_surname1')}
                        readOnly={isReadOnly("surName1")}
                        style={{width: "100%"}}
                      />
                    </Grid>
                  )}
                  {!company && (
                    <Grid item xs={12} md={4}>
                      <Field
                        name="surName2"
                        component={TextField}
                        label={t('common:text.contractation_surname2')}
                        readOnly={isReadOnly("surName2")}
                        style={{width: "100%"}}
                      />
                    </Grid>
                  )}
                  <Grid container item xs={12}>
                    <Grid item xs={12} md={4}>
                      <Field
                        name="mobile"
                        render={({input, meta}) => <NoFlagMuiTelInputStyled
                          disableDropdown={true}
                          disabled={isReadOnly("mobile")}
                          inputProps={{ maxLength: 12 }}
                          label={t('common:text.lead_contact_telephone')}
                          forceCallingCode={true}
                          defaultCountry="ES"
                          fullWidth={true}
                          helperText={meta.touched && meta.error}
                          error={meta.touched && !!meta.error}
                          {...input}
                        />} 
                        label={t('common:text.contractation_mobile_phone')}
                        readOnly={isReadOnly("mobile")}
                        style={{width: "100%"}}
                      />
                    </Grid>
                  </Grid>
                  <Grid container item xs={12}>
                    <Grid item xs={12} md={4}>
                      <Field
                        name="email"
                        component={EmailField}
                        label={
                          t('common:text.contractation_email')
                          + `${(Settings?.contractation?.electricity?.emailRequired ?? true) ? "*" : ""}`
                        }
                        readOnly={isReadOnly("email")}
                        style={{width: "100%"}}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider/>
                  </Grid>
                </Grid>
                {company &&
                  <>
                    <Grid container spacing={3} item xs={12} sx={{pr: 0, pt: 0}}>
                      <Grid item xs={12} sx={{pb: {md: 0}}}>
                        <Typography variant="h6">
                          {t('common:text.contractation_legal_representative')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4} style={{paddingRight: 0}}>
                        <Field
                          name="vatRepresentante"
                          render={({input: {onChange, value}, meta}) => {
                            return (
                              <VATField
                                value={value}
                                onBlur={(event) => onChange(event)}
                                onChange={onChange}
                                error={meta.error}
                                showError={meta.touched}
                                style={{ width: "100%" }}
                              />
                            )
                          }}
                          label={t('common:text.contractation_nif')}
                          style={{width: "100%"}}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Field
                        name="nameRepresentante"
                        component={TextField}
                        label={t('common:text.contractation_legal_representative_name')}
                        style={{width: "100%"}}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Field
                        name="surName1Representante"
                        component={TextField}
                        label={t('common:text.contractation_legal_representative_surname1')}
                        style={{width: "100%"}}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Field
                        name="surName2Representante"
                        component={TextField}
                        label={t('common:text.contractation_legal_representative_surname2')}
                        style={{width: "100%"}}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Paper style={{padding: 15}} variant="outlined">
                        <Field
                          name="nifFaceup"
                          render={({input, meta}) => <FileUploadField
                            input={input}
                            min={1}
                            max={2}
                            label={t('common:text.contractation_dni_front') + "*"}
                            hint={t('common:text.contractation_doc_invoice_helper', {doc: 'DNI'})}
                            anotherLabel={t('common:text.contractation_dni_back')}
                            removeLabel={t('common:text.remove')}
                            meta={{error: [meta.error], touched: meta.touched}}
                          />}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider/>
                    </Grid>
                  </>
                }
                {contractation.steps.installation.activeContract === true && <Grid item xs={12}>
                  <Paper elevation={3} style={{padding: 15}}>
                    <Typography variant="h6">
                      { ((Settings?.contractation?.nameInChangeOwnerQuestion ?? false) && 
                        (Object.values(contractation.config).some(val => typeof val == "string" && !!val))) ?
                        t("common:text.contractation_change_owner_question_named", {
                          owner: contractation.config.name + " " + 
                            contractation.config.surName1 + " " + 
                            contractation.config.surName2
                        })
                        : t("common:text.contractation_change_owner_question")
                      }
                    </Typography>
                    <Field
                      name="changeOwner"
                      render={({input, meta}) => <RadioGroupField 
                        input={{
                          ...input,
                          onChange: (value) => {
                            input.onChange(JSON.parse(value))
                          }
                        }}
                        meta={meta}
                        radioElements={
                          [
                            {label: t("common:text.generic_yes"), val: false, style: {width: "25%"}},
                            {label: t("common:text.generic_no"), val: true, style: {width: "25%"}}
                          ]
                        }
                        row
                      />}
                    />
                  </Paper>
                </Grid>
                }
                {(Settings.contractation.commsLanguage ?? true) &&
                  <Grid item xs={12} md={6}>
                    <Field name="commsLanguage" component={LangSelectField} />
                  </Grid>
                }
                <Grid item xs={12} style={{paddingBottom: 0}}>
                  <Divider style={{marginBottom: 10}}/>
                  <Field
                    name="sameInvoiceAddress"
                    label={t('common:text.contractation_same_address')}
                    component={CheckboxField}
                  />
                  {!values.sameInvoiceAddress && (
                    <Grid item xs={12}>
                      <Field
                        name="ownerAddress"
                        label={t('common:text.contractation_invoicing_address')}
                      >
                        {props => {
                            // return <div>Test</div>
                            return <FormProvider {...invoiceAddressMethods}>
                              <AddressForm 
                                name="ownerAddress" 
                                input={props.input}
                              />
                            </FormProvider>
                          }
                      }
                      </Field>
                    </Grid>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Divider style={{marginBottom: 20}}/>
                  <Typography variant="h6">
                    {t('common:text.contractation_doc_photos', 
                      {doc: company ? t('common:text.contractation_doc_photo_cif') : "DNI"})
                    }
                  </Typography>
                  <Paper style={{padding: 15}} variant="outlined">
                    {company ? 
                    <Field
                      name="nifEmpresa"
                      render={({input, meta}) => <FileUploadField
                        input={input}
                        min={1}
                        label={t('common:text.contractation_nif_doc') + "*"}
                        hint={
                          t('common:text.contractation_doc_invoice_helper',
                          {doc: t('common:text.contractation_doc_photo_cif')})
                        }
                        anotherLabel={t('common:text.contractation_nif_add')}
                        removeLabel={t('common:text.remove')}
                        meta={{error: [meta.error], touched: meta.touched}}
                      />}
                    />
                    :
                    <Field
                      name="nifFaceup"
                      render={({input, meta}) => <FileUploadField
                        input={input}
                        min={1}
                        max={2}
                        label={
                          t('common:text.contractation_dni_front') +
                          (Settings?.contractation?.dniRequired !== false ? '*' : '')
                        }
                        hint={t('common:text.contractation_doc_invoice_helper', {doc: 'DNI'})}
                        anotherLabel={t('common:text.contractation_dni_back')}
                        removeLabel={t('common:text.remove')}
                        meta={{error: [meta.error], touched: meta.touched}}
                      />}
                    />  
                  }
                  </Paper>
                </Grid>
              </Grid>
            </Box>
            
            <Stack sx={{mt: 3}} gap={1}>
              { contractation.leadErrors.badScore && 
                <Alert severity={"error"} variant="filled">
                  {t("common:text.contractation_leads_bad_score")}
                  <br></br>
                  {t("common:text.contractation_leads_bad_score_thanks")}
                </Alert>
              }
              { contractation.leadErrors.dniInvalid &&
                <Alert severity={"error"} variant="filled">
                  {t("common:text.contractation_leads_dni_invalid")}
                </Alert>
              }
            </Stack>
          </form>
        );
      }}
    />
  )
};

export default FormPerson;
